import api from './api';
import type { DriverWithMoves } from '../components/driver-status-monitor/types';

export interface CurrentTruck {
  unitNumber: string;
  truckRecno: number;
  truckVin: string;
  eldReferenceNum: string;
  ownerRecno: number;
}

export interface CurrentDriver {
  unitNumber: string;
  ownerRecno: number;
  driverRecno: number;
  driverLicense: string;
  driverLicenseState: string;
  firstName: string;
  lastName: string;
  driverActive: string;
  unitStatus: string;
}

export interface DriverDispatch {
  pronumber: string;
  legdrop?: string;
  legtype?: string;
  legaction2?: string;
  dispatchStarted: string | null;
  originName: string;
  destName: string;
  origApptDate: string | null;
  origApptTime1: string | null;
  origApptTime2: string | null;
  origArrivalDateTime: string | null;
  origDepartDateTime: string | null;
  destApptDate: string | null;
  destApptTime1: string | null;
  destApptTime2: string | null;
  arrivalDateTime: string | null;
  departDateTime: string | null;
  isComplete: boolean;
  isStarted: boolean;
}

export interface CurrentDispatchesResponse {
  unitNumber: string;
  dispatchCount: number;
  dispatches: DriverDispatch[];
}

export interface DriverDispatchHistoryRow {
  pronumber: number;
  legrecno: number;
  // Null when the leg is driver-assigned but has no legdispatch row — the
  // history reads legsall.driver, which does not require a dispatch record.
  dispatchrecno: number | null;
  legnum: number;
  legdrop: string | null;
  legtype: string | null;
  legaction2: string | null;
  legstatus: string | null;
  legmiles: number | null;
  container: string | null;
  sizeType: string | null;
  triaxle: string | null;
  swing: string | null;
  otr: string | null;
  sendtogwimove: string | null;
  acceptreject: string | null;
  dispatchStarted: string | null;
  dispatcherUsername: string | null;
  dispatchSentAt: string | null;
  originName: string | null;
  origCityState: string | null;
  destName: string | null;
  destCityState: string | null;
  origcode: number | null;
  destcode: number | null;
  origApptDate: string | null;
  origApptTime1: string | null;
  origApptTime2: string | null;
  origArrivalDateTime: string | null;
  origDepartDateTime: string | null;
  destApptDate: string | null;
  destApptTime1: string | null;
  destApptTime2: string | null;
  arrivalDateTime: string | null;
  departDateTime: string | null;
  isComplete: boolean;
  isStarted: boolean;
  isInTransit: boolean;
  estimateddriverpay: number | null;
  dispatch_priority: number | null;
}

export interface DriverDispatchHistoryResponse {
  unitNumber: string;
  driverName: string | null;
  isMule?: boolean;
  startDate: string;
  endDate: string;
  dispatchCount: number;
  dispatches: DriverDispatchHistoryRow[];
}

class UnitsService {
  async getCurrentTruck(licenseNumber: string, licenseState: string) {
    return api.get<{ data: CurrentTruck }>(`/units/getCurrentTruck`, {
      params: {
        license_number: licenseNumber,
        license_state: licenseState,
      },
    });
  }

  async getCurrentDriver(truckVin?: string, eldReferenceNum?: string) {
    return api.get<{ data: CurrentDriver }>(`/units/getCurrentDriver`, {
      params: {
        truck_vin: truckVin,
        eld_reference_num: eldReferenceNum,
      },
    });
  }

  async getDriverCurrentDispatches(unitNumber: string) {
    return api.get<CurrentDispatchesResponse>(`/driver-dispatch/driver-current-dispatches/${unitNumber}`);
  }

  async getDriverDispatchHistory(
    unitNumber: string,
    startDate: string,
    endDate: string,
    additionalFields?: string[],
  ) {
    return api.get<DriverDispatchHistoryResponse>(
      `/driver-dispatch/driver-dispatch-history/${unitNumber}`,
      {
        params: {
          start_date: startDate,
          end_date: endDate,
          // Unsorted: the backend keeps the user's selection order.
          ...(additionalFields?.length ? { additional_fields: additionalFields.join(',') } : {}),
        },
      },
    );
  }

  async getDriver(unitNumber: string, startDate?: string, endDate?: string) {
    return api.get<DriverWithMoves>(`/driver-status-monitor/drivers/${unitNumber}`, {
      params: startDate ? { start_date: startDate, end_date: endDate ?? startDate } : undefined,
    });
  }

  async reorderDriverMoves(unitNumber: string, moves: { pronumber: number; legnum: number; priority: number }[]) {
    return api.post('/driver-status-monitor/reorder-moves', {
      driver_unitnumber: unitNumber,
      moves,
    });
  }
}

export default new UnitsService();
